import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { filter, map } from 'rxjs';

import { SegmentTrackEvent } from '@ninety/platform/models/analytics/segment/segment-track-event.enum';
import { MeetingIssuesEvents, TeamListStateActions } from '@ninety/platform/state';
import { SegmentActions } from '@ninety/platform/state/app-global/analytics/segment/segment.actions';
import { IssuesActions as IssuesV2Actions } from '@ninety/web/pages/issues-v2/_state/issues.actions';

import {
  MeetingConcludeActions,
  MeetingDialogActions,
  MeetingSchedulingActions,
  MeetingsAgendasV2Actions,
  MeetingsPageActions,
  MeetingsTabsActions,
  MeetingStateActions,
} from '../meetings.actions';
import { MeetingsStateSelectors } from '../meetings.selectors';

@Injectable()
export class MeetingsAnalyticsEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly router = inject(Router);

  private static readonly MEETING_ISSUES_ROUTE_RE = /^\/meeting\/[^/]+\/[^/]+\/issues\/(short-term|long-term)\b/;

  /** Meetings Page Viewed — fires when meetings tabs component initializes. teamId comes from
   *  selectSelectedTeam (the filter-bar team the page is scoped to), as in the agenda effects
   *  here; undefined if the team hasn't resolved yet on a direct navigation. */
  trackMeetingsPageViewed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingsTabsActions.init),
      concatLatestFrom(() => this.store.select(MeetingsStateSelectors.selectSelectedTeam)),
      map(([, team]) =>
        SegmentActions.track({
          event: SegmentTrackEvent.MEETINGS_PAGE_VIEWED,
          params: {
            teamId: team?._id,
          },
        })
      )
    )
  );

  /** Meeting Navigation Toggled — fires when the user expands/collapses the meeting side
   *  navigation (collapse from the sidenav's toggle, expand from the filters toolbar).
   *  `expanded` is the resulting state. */
  trackMeetingNavToggled$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingsPageActions.meetingNavToggled),
      map(({ expanded }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.MEETING_NAVIGATION_TOGGLED,
          params: { expanded },
        })
      )
    )
  );

  /** RNA Meeting Panel Join Clicked — the My90 meeting panel's "Join your meeting" CTA. Fires on
   *  the click (the action), not on the join succeeding, so an abandoned/failed join still counts
   *  as intent. `my90` is the only surface this action is dispatched from today. */
  trackMeetingPanelJoinClicked$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingsPageActions.joinActiveMeeting),
      map(({ meeting }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.RNA_MEETING_PANEL_JOIN_CLICKED,
          params: {
            entityId: meeting._id,
            teamId: meeting.teamId,
            meetingType: meeting.type,
            eventSource: 'my90',
          },
        })
      )
    )
  );

  /** RNA Meeting Panel Schedule Clicked — the My90 meeting panel's "Schedule" empty-state CTA.
   *  The open action is also dispatched by the Meetings page, which names no team; filtering on
   *  teamId keeps this to the My90 entry point so `eventSource` stays truthful. */
  trackMeetingPanelScheduleClicked$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingSchedulingActions.openScheduleMeetingDialog),
      filter(({ teamId }) => !!teamId),
      map(({ teamId }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.RNA_MEETING_PANEL_SCHEDULE_CLICKED,
          params: {
            teamId,
            eventSource: 'my90',
          },
        })
      )
    )
  );

  /** Meeting Started — fires when a meeting is successfully created/started */
  trackMeetingStarted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingDialogActions.createMeetingSuccess),
      map(({ meeting }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.MEETING_STARTED,
          params: {
            entityId: meeting._id,
            meetingType: meeting.type,
            teamId: meeting.teamId,
          },
        })
      )
    )
  );

  /** Meeting Concluded — fires when a meeting is concluded. Reads entityId/teamId/meetingType
   *  from the action payload because the reducer that handles this same action clears
   *  currentMeeting to null; sampling the store via concatLatestFrom here would race and
   *  emit undefined fields. */
  trackMeetingConcluded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingConcludeActions.meetingConcluded),
      map(({ entityId, teamId, meetingType }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.MEETING_CONCLUDED,
          params: {
            entityId,
            meetingType,
            teamId,
          },
        })
      )
    )
  );

  /** Meeting Issue Worked — fires when an issue's completed state is toggled while a meeting is
   *  active. Triggers on the same MeetingIssuesEvents.setCompletedState bridge the worked-issue
   *  business effect consumes; the !!meeting guard scopes it to in-meeting toggles (the bridge
   *  also fires for completions outside a meeting). Parent context pulled from the current meeting. */
  trackMeetingIssueWorked$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingIssuesEvents.setCompletedState),
      concatLatestFrom(() => this.store.select(MeetingsStateSelectors.selectCurrentMeeting)),
      filter(([, meeting]) => !!meeting),
      map(([{ issueId, completed }, meeting]) =>
        SegmentActions.track({
          event: SegmentTrackEvent.MEETING_ISSUE_WORKED,
          params: {
            entityId: issueId,
            parentEntityID: meeting?._id,
            meetingType: meeting?.type,
            teamId: meeting?.teamId,
            isCompleted: completed,
          },
        })
      )
    )
  );

  /** Meeting Agenda Created — fires when a new custom team agenda is saved.
   *  teamId pulled from selectSelectedTeam since the success action carries only the agenda. */
  trackMeetingAgendaCreated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingsAgendasV2Actions.saveNewCustomAgendaSuccess),
      concatLatestFrom(() => this.store.select(MeetingsStateSelectors.selectSelectedTeam)),
      map(([{ agenda }, team]) =>
        SegmentActions.track({
          event: SegmentTrackEvent.MEETING_AGENDA_CREATED,
          params: {
            entityId: agenda._id,
            parentEntityID: team?._id,
            teamId: team?._id,
          },
        })
      )
    )
  );

  /** Meeting Joined — fires when a user clicks Join on an active meeting.
   *  Listens to getMeetingSuccess (Join button → getMeeting → API → getMeetingSuccess).
   *  Resume flow uses a sibling resumeMeeting action and is intentionally not tracked here;
   *  BI can request a meeting_resumed event separately if needed. */
  trackMeetingJoined$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingsPageActions.getMeetingSuccess),
      map(({ meeting }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.MEETING_JOINED,
          params: {
            entityId: meeting._id,
            meetingType: meeting.type,
            teamId: meeting.teamId,
          },
        })
      )
    )
  );

  /** Meeting Agenda Printed — fires when a user prints a meeting agenda (default or custom).
   *  Triggered from the meetings-page Agendas tab — not from inside an active meeting,
   *  so parentEntity='team' to reflect where the print actually happens.
   *  entityId is populated only when printing a custom agenda (customAgendaId is undefined
   *  for default agenda types like weekly/l10/quarterly/annual). */
  trackMeetingAgendaPrinted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingDialogActions.printMeetingAgenda),
      concatLatestFrom(() => this.store.select(MeetingsStateSelectors.selectSelectedTeam)),
      map(([{ meetingType, customAgendaId }, team]) =>
        SegmentActions.track({
          event: SegmentTrackEvent.MEETING_AGENDA_PRINTED,
          params: {
            entityId: customAgendaId,
            parentEntityID: team?._id,
            meetingType,
            teamId: team?._id,
          },
        })
      )
    )
  );

  /** Meeting Agenda Updated — fires when a team agenda update is confirmed by the team-list API.
   *  Both create-new-custom and update-existing flows pass through TeamListStateActions.updateTeamMeetingAgenda,
   *  so we filter on update.newAgenda to keep this effect scoped to true updates.
   *  MeetingsAgendasV2Actions.updateAgendaSuccess is defined in the action group but never dispatched. */
  trackMeetingAgendaUpdated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamListStateActions.updateTeamMeetingAgendaSuccess),
      filter(({ update }) => !update.newAgenda),
      concatLatestFrom(() => this.store.select(MeetingsStateSelectors.selectSelectedTeamAgenda)),
      map(([{ update }, agenda]) =>
        SegmentActions.track({
          event: SegmentTrackEvent.MEETING_AGENDA_UPDATED,
          params: {
            entityId: agenda?._id,
            parentEntityID: update.teamId,
            teamId: update.teamId,
          },
        })
      )
    )
  );

  /** Meeting Tangent Clicked — fires when a user clicks the tangent button during a meeting. */
  trackMeetingTangentClicked$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingsPageActions.tangentButtonClicked),
      concatLatestFrom(() => this.store.select(MeetingsStateSelectors.selectCurrentMeeting)),
      map(([, meeting]) =>
        SegmentActions.track({
          event: SegmentTrackEvent.MEETING_TANGENT_CLICKED,
          params: {
            entityId: meeting?._id,
            parentEntityID: meeting?._id,
            meetingType: meeting?.type,
            teamId: meeting?.teamId,
          },
        })
      )
    )
  );

  /** Meeting Presenter Changed — fires when a user claims the presenter role.
   *  claimPresenter is the intent action; the service call inside meetings.effects.ts:claimPresenter$
   *  doesn't dispatch a success action (same dead-success pattern as handleWorkedIssue and
   *  updateAgendaSuccess), so we track on intent. */
  trackMeetingPresenterChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingStateActions.claimPresenter),
      concatLatestFrom(() => this.store.select(MeetingsStateSelectors.selectCurrentMeeting)),
      map(([, meeting]) =>
        SegmentActions.track({
          event: SegmentTrackEvent.MEETING_PRESENTER_CHANGED,
          params: {
            entityId: meeting?._id,
            parentEntityID: meeting?._id,
            meetingType: meeting?.type,
            teamId: meeting?.teamId,
          },
        })
      )
    )
  );

  /** Meeting Issue Voted — fires when a user adds a vote to an issue WHILE in an active meeting.
   *  Votes outside of meetings are filtered out via the currentMeeting.inProgress check. */
  trackMeetingIssueVoted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesV2Actions.addVoteSuccess),
      concatLatestFrom(() => this.store.select(MeetingsStateSelectors.selectCurrentMeeting)),
      filter(([, meeting]) => !!meeting?.inProgress),
      map(([{ issue }, meeting]) =>
        SegmentActions.track({
          event: SegmentTrackEvent.MEETING_ISSUE_VOTED,
          params: {
            entityId: issue._id,
            userId: issue.userId,
            parentEntityID: meeting?._id,
            meetingType: meeting?.type,
            teamId: meeting?.teamId,
          },
        })
      )
    )
  );

  /** Fires on the presenter's toggle, before the save — there is no success action to hook. */
  trackMeetingIssueVotingToggled$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingIssuesEvents.toggleVotingEnabled),
      concatLatestFrom(() => this.store.select(MeetingsStateSelectors.selectCurrentMeeting)),
      map(([{ votingEnabled }, meeting]) =>
        SegmentActions.track({
          event: SegmentTrackEvent.MEETING_ISSUE_VOTING_TOGGLED,
          params: {
            entityId: meeting?._id,
            teamId: meeting?.teamId,
            meetingType: meeting?.type,
            isVotingEnabled: votingEnabled,
          },
        })
      )
    )
  );

  /** Meeting Top Three Issues Selected — fires when a user confirms a Top 3 selection during a meeting.
   *  Action operates on a set of issues, so entityId is omitted and the 3 issue IDs flow through
   *  as an issueIds property. Filter on meeting.inProgress to scope to in-meeting selections. */
  trackMeetingTopThreeIssuesSelected$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesV2Actions.selectTopThreeIssuesSuccess),
      concatLatestFrom(() => this.store.select(MeetingsStateSelectors.selectCurrentMeeting)),
      filter(([, meeting]) => !!meeting?.inProgress),
      map(([{ issues }, meeting]) =>
        SegmentActions.track({
          event: SegmentTrackEvent.MEETING_TOP_THREE_ISSUES_SELECTED,
          params: {
            parentEntityID: meeting?._id,
            meetingType: meeting?.type,
            teamId: meeting?.teamId,
            issueIds: issues.map(i => i._id),
          },
        })
      )
    )
  );

  /** Meeting Notes Used — fires on every toggle of the in-meeting notes panel (open AND close).
   *  Dispatched from meeting-sidenav-v2.component.ts:onNotesToggled() alongside the service call.
   *  visible carries the post-toggle state so BI can filter to opens-only if desired. */
  trackMeetingNotesUsed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingsPageActions.toggleNotesPanel),
      concatLatestFrom(() => this.store.select(MeetingsStateSelectors.selectCurrentMeeting)),
      map(([{ visible }, meeting]) =>
        SegmentActions.track({
          event: SegmentTrackEvent.MEETING_NOTES_USED,
          params: {
            entityId: meeting?._id,
            parentEntityID: meeting?._id,
            meetingType: meeting?.type,
            teamId: meeting?.teamId,
            visible,
          },
        })
      )
    )
  );

  /** Meeting Issues Column Viewed — fires when a user navigates to the issues column inside
   *  an active meeting. Listens to Router NavigationEnd events and filters by URL path so the
   *  effect doesn't require a cross-lib dispatch from issues-v2. The currentMeeting.inProgress
   *  check guards against the rare case where the meeting state hasn't loaded yet on direct
   *  navigation. isShortTerm derived from the URL slug. */
  trackMeetingIssuesColumnViewed$ = createEffect(() =>
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      filter(event => MeetingsAnalyticsEffects.MEETING_ISSUES_ROUTE_RE.test(event.urlAfterRedirects)),
      concatLatestFrom(() => this.store.select(MeetingsStateSelectors.selectCurrentMeeting)),
      filter(([, meeting]) => !!meeting?.inProgress),
      map(([event, meeting]) =>
        SegmentActions.track({
          event: SegmentTrackEvent.MEETING_ISSUES_COLUMN_VIEWED,
          params: {
            entityId: meeting?._id,
            parentEntityID: meeting?._id,
            meetingType: meeting?.type,
            teamId: meeting?.teamId,
            isShortTerm: event.urlAfterRedirects.includes('/short-term'),
          },
        })
      )
    )
  );
}
